import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { form, FormField as SignalFormField } from '@angular/forms/signals';
import {
  AppSvgIconComponent,
  OverlayService,
  SignalButton,
  SignalCheckbox,
  SignalDropdown,
  SignalFormDirective,
  SignalTextField,
} from '@vallift/ngl';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';
import type {
  FormField,
  FormFieldDataLibraryCollectionRef,
} from '../../../../models/built-form';
import {
  LinkCollectionButtonComponent,
  type LinkedCollectionResult,
} from '../../../link-collection-button/link-collection-button';
import {
  CollectionColumnPickerOverlayComponent,
  type CollectionColumnPickerResult,
} from '../../../collection-column-picker-overlay/collection-column-picker-overlay';
import {
  resolveCollectionDropdownItems,
  type CollectionSelectOption,
} from '../../../../utils/resolve-collection-dropdown-items';

interface SingleChoiceConfigModel {
  label: string;
  helpText: string;
  options: string[];
  defaultValue: string;
  required: boolean;
  disabled: boolean;
}

interface ColumnOption {
  key: string;
  label: string;
}

@Component({
  selector: 'app-single-choice-config-view',
  standalone: true,
  templateUrl: './single-choice-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SignalCheckbox,
    SignalFormDirective,
    SignalTextField,
    SignalFormField,
    SignalDropdown,
    SignalButton,
    AppSvgIconComponent,
    LinkCollectionButtonComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
  ],
})
export class SingleChoiceConfigViewComponent {
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<SingleChoiceConfigModel>({
    label: '',
    helpText: '',
    options: ['Option 1', 'Option 2', 'Option 3'],
    defaultValue: '',
    required: false,
    disabled: false,
  });

  readonly linkedCollection = signal<FormFieldDataLibraryCollectionRef | null>(
    null,
  );
  readonly isCollectionLinked = computed(() => this.linkedCollection() != null);

  readonly collectionColumns = signal<ColumnOption[]>([]);
  readonly collectionDefaultItems = signal<CollectionSelectOption[]>([]);

  readonly showAsLabel = computed(() => {
    const linked = this.linkedCollection();
    if (!linked?.displayFieldKey) {
      return '';
    }
    return (
      linked.displayFieldLabel ||
      this.collectionColumns().find(
        (column) => column.key === linked.displayFieldKey,
      )?.label ||
      linked.displayFieldKey
    );
  });

  readonly defaultOptionItems = computed(() => {
    const seen = new Set<string>();
    const items: { label: string; value: string }[] = [];
    for (const option of this.model().options) {
      const value = option.trim();
      if (!value || seen.has(value)) {
        continue;
      }
      seen.add(value);
      items.push({ label: value, value });
    }
    return items;
  });

  readonly configForm = form(this.model);

  private lastFieldId: string | null = null;
  private suppressEmit = false;
  private lastLoadedCollectionId: string | null = null;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      const collection = next.dataLibraryCollection;
      this.linkedCollection.set(
        collection?.dataLibraryId
          ? {
              dataLibraryId: collection.dataLibraryId,
              displayFieldKey: collection.displayFieldKey,
              displayFieldLabel: collection.displayFieldLabel,
              label: collection.label,
              key: collection.key,
            }
          : null,
      );

      const legacyConfigOptions =
        next.config && 'options' in next.config && Array.isArray(next.config.options)
          ? next.config.options.filter(
              (option): option is string => typeof option === 'string',
            )
          : null;

      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        options: collection?.dataLibraryId
          ? ['', '']
          : next.options?.length
            ? [...next.options]
            : (legacyConfigOptions ?? ['Option 1', 'Option 2', 'Option 3']),
        defaultValue:
          typeof next.config?.defaultValue === 'string'
            ? next.config.defaultValue
            : '',
        required: next.required ?? validation.required ?? false,
        disabled: next.disabled === true,
      });
    });

    effect(() => {
      const linked = this.linkedCollection();
      const collectionId = linked?.dataLibraryId?.trim() ?? '';
      if (!collectionId) {
        this.collectionColumns.set([]);
        this.collectionDefaultItems.set([]);
        this.lastLoadedCollectionId = null;
        return;
      }
      if (collectionId === this.lastLoadedCollectionId) {
        return;
      }
      this.lastLoadedCollectionId = collectionId;
      this.loadCollectionMetadata(collectionId);
    });

    effect((onCleanup) => {
      const linked = this.linkedCollection();
      const collectionId = linked?.dataLibraryId?.trim() ?? '';
      const displayFieldKey = linked?.displayFieldKey?.trim() ?? '';
      if (!collectionId || !displayFieldKey) {
        this.collectionDefaultItems.set([]);
        return;
      }
      const sub = resolveCollectionDropdownItems(
        this.workspaceApi,
        collectionId,
        displayFieldKey,
      ).subscribe({
        next: (items) => {
          this.collectionDefaultItems.set(items);
          const current = this.model().defaultValue.trim();
          if (current && !items.some((item) => item.id === current)) {
            this.model.update((model) => ({ ...model, defaultValue: '' }));
          }
        },
        error: () => this.collectionDefaultItems.set([]),
      });
      onCleanup(() => sub.unsubscribe());
    });

    effect(() => {
      const draft = this.model();
      const linked = this.linkedCollection();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      const options = linked
        ? undefined
        : draft.options.map((option) => option.trim()).filter(Boolean);

      const trimmedDefault = draft.defaultValue.trim();
      const defaultValue = linked
        ? trimmedDefault || undefined
        : options?.includes(trimmedDefault)
          ? trimmedDefault
          : undefined;

      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        config: {
          ...field.config,
          defaultValue,
          options: undefined,
        },
        default: defaultValue
          ? { source: 'static', value: defaultValue }
          : undefined,
        validation: {
          ...field.validation,
          required: draft.required,
        },
        required: draft.required,
        disabled: draft.disabled,
        ...(linked
          ? {
              options: undefined,
              dataLibraryCollection: {
                dataLibraryId: linked.dataLibraryId,
                displayFieldKey: linked.displayFieldKey,
                displayFieldLabel: linked.displayFieldLabel,
                label: linked.label,
                key: linked.key,
              },
            }
          : {
              options,
              dataLibraryCollection: undefined,
            }),
      });
    });
  }

  addOption(): void {
    if (this.isCollectionLinked()) {
      return;
    }
    this.model.update((model) => ({
      ...model,
      options: [...model.options, ''],
    }));
  }

  removeOption(index: number): void {
    if (this.isCollectionLinked()) {
      return;
    }
    this.model.update((model) => {
      if (model.options.length <= 2) {
        return model;
      }
      const removed = model.options[index]?.trim() ?? '';
      const options = model.options.filter((_, i) => i !== index);
      const defaultValue =
        removed && model.defaultValue.trim() === removed
          ? ''
          : model.defaultValue;
      return { ...model, options, defaultValue };
    });
  }

  onOptionDrop(event: CdkDragDrop<string[]>): void {
    if (this.isCollectionLinked()) {
      return;
    }
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.model.update((model) => {
      const options = [...model.options];
      moveItemInArray(options, event.previousIndex, event.currentIndex);
      return { ...model, options };
    });
  }

  onDefaultValueChange(value: unknown): void {
    const next =
      typeof value === 'string' ? value.trim() : value == null ? '' : String(value);
    this.model.update((model) => ({
      ...model,
      defaultValue: next,
    }));
  }

  onCollectionSelected(result: LinkedCollectionResult): void {
    this.suppressEmit = false;
    this.lastLoadedCollectionId = null;
    this.linkedCollection.set({
      dataLibraryId: result.id,
      displayFieldKey: result.displayFieldKey,
      displayFieldLabel: result.displayFieldLabel,
      label: result.name,
      key: result.key,
    });
    this.model.update((model) => ({
      ...model,
      options: ['', ''],
      defaultValue: result.defaultItemId?.trim() || '',
    }));
  }

  onDisplayFieldKeyChange(value: unknown): void {
    const key = typeof value === 'string' ? value.trim() : '';
    if (!key) {
      return;
    }
    const label =
      this.collectionColumns().find((column) => column.key === key)?.label ||
      key;
    this.linkedCollection.update((linked) =>
      linked
        ? { ...linked, displayFieldKey: key, displayFieldLabel: label }
        : linked,
    );
  }

  changeShowAsColumn(): void {
    const linked = this.linkedCollection();
    if (!linked?.dataLibraryId) {
      return;
    }
    const dialogRef = this.overlayService.openModal(
      CollectionColumnPickerOverlayComponent,
      {
        fitContent: true,
        panelClass: ['w-[22rem]', 'max-w-[calc(100%-2rem)]'],
        data: {
          collectionId: linked.dataLibraryId,
          collectionName: linked.label,
          selectedDisplayFieldKey: linked.displayFieldKey,
          selectedDefaultItemId: this.model().defaultValue.trim() || undefined,
        },
      },
    );
    dialogRef.closed.subscribe(
      (result: CollectionColumnPickerResult | null | undefined) => {
        if (!result?.displayFieldKey) {
          return;
        }
        this.linkedCollection.update((current) =>
          current
            ? {
                ...current,
                displayFieldKey: result.displayFieldKey,
                displayFieldLabel: result.displayFieldLabel,
              }
            : current,
        );
        this.model.update((model) => ({
          ...model,
          defaultValue: result.defaultItemId?.trim() || '',
        }));
      },
    );
  }

  detachLinkedCollection(): void {
    this.unlinkCollectionAndMaterializeOptions();
  }

  removeLinkedCollection(): void {
    this.unlinkCollectionAndMaterializeOptions();
  }

  private unlinkCollectionAndMaterializeOptions(): void {
    const { options, defaultValue } = this.materializeOptionsFromCollection();
    this.linkedCollection.set(null);
    this.collectionColumns.set([]);
    this.collectionDefaultItems.set([]);
    this.lastLoadedCollectionId = null;
    this.model.update((model) => ({
      ...model,
      options,
      defaultValue,
    }));
  }

  private materializeOptionsFromCollection(): {
    options: string[];
    defaultValue: string;
  } {
    const items = this.collectionDefaultItems();
    const options = items.map((item) => item.display.trim()).filter(Boolean);
    const defaultId = this.model().defaultValue.trim();
    const defaultDisplay = defaultId
      ? items.find((item) => item.id === defaultId)?.display.trim() || ''
      : '';
    return {
      options: options.length > 0 ? options : ['Option 1', 'Option 2', 'Option 3'],
      defaultValue: defaultDisplay,
    };
  }

  private loadCollectionMetadata(collectionId: string): void {
    this.workspaceApi.getDataLibraryCollection(collectionId).subscribe({
      next: (response) => {
        const data = response.data;
        if (!data || this.linkedCollection()?.dataLibraryId !== collectionId) {
          return;
        }
        const columns = (data.fields ?? []).map(
          (field: DataLibraryCollectionFieldResponse): ColumnOption => ({
            key: field.key,
            label: field.name || field.key,
          }),
        );
        this.collectionColumns.set(columns);

        const linked = this.linkedCollection();
        const key = linked?.displayFieldKey?.trim();
        if (key && !linked?.displayFieldLabel) {
          const label = columns.find((column) => column.key === key)?.label;
          if (label) {
            this.suppressEmit = true;
            this.linkedCollection.update((current) =>
              current ? { ...current, displayFieldLabel: label } : current,
            );
          }
        }
      },
      error: () => {
        if (this.linkedCollection()?.dataLibraryId === collectionId) {
          this.collectionColumns.set([]);
        }
      },
    });
  }
}
